import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Stack,
  Chip,
  TextField,
  Tooltip,
  useTheme,
  IconButton,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { tournamentApi, seriesApi } from '../../services/api';
import CloseIcon from '@mui/icons-material/Close';
import { format } from 'date-fns';

const PLATFORMS = [
  { id: 'espn', label: 'ESPN' },
  { id: 'd11', label: 'Dream11' },
  { id: 'c11', label: 'My11Circle' },
  { id: 'cricbuzz', label: 'Cricbuzz' },
  { id: 'crex', label: 'CREX' },
  { id: 'f11', label: 'MyFab11' },
  { id: 'mt11', label: 'MyTeam11' },
  { id: 'cb11', label: 'Cricbuzz11' },
];

const NewSeries: React.FC = () => {
  const [options, setOptions] = useState<{ [platform: string]: any[] }>({});
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{ id: number; name: string } | null>(null);
  const [selected, setSelected] = useState<{ [platform: string]: { id: string; tournament: any } }>({});
  const [search, setSearch] = useState('');
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();

  useEffect(() => {
    setLoading(true);
    setError(null);
    tournamentApi.getRecent('cricket')
      .then(res => {
        const grouped: { [platform: string]: any[] } = {};
        (res.data.results || res.data).forEach((t: any) => {
          if (!grouped[t.platform]) grouped[t.platform] = [];
          grouped[t.platform].push(t);
        });
        setOptions(grouped);
        setLoading(false);
      })
      .catch(() => {
        setError('Failed to fetch available tournaments');
        setLoading(false);
      });
  }, []);

  // Filtered and sorted options by search and attachment
  const filteredOptions = useMemo(() => {
    const lower = search.trim().toLowerCase();
    const filtered: { [platform: string]: any[] } = {};
    for (const p of PLATFORMS) {
      let list = (options[p.id] || []);
      if (lower) {
        list = list.filter((item: any) => item.name?.toLowerCase().includes(lower));
      }
      // Sort: unattached first, then attached
      list = [...list].sort((a, b) => {
        const aUnattached = !a.series_id;
        const bUnattached = !b.series_id;
        if (aUnattached === bUnattached) return 0;
        return aUnattached ? -1 : 1;
      });
      filtered[p.id] = list;
    }
    return filtered;
  }, [search, options]);

  const handleSelect = (platform: string, id: string, tournament?: any) => {
    setSelected(prev => ({ ...prev, [platform]: { id, tournament } }));
    setSuccess(null);
    setError(null);
  };

  const handleDeselect = (platform: string) => {
    setSelected(prev => {
      const copy = { ...prev };
      delete copy[platform];
      return copy;
    });
  };

  const handleCreate = async () => {
    if (Object.keys(selected).length === 0) return;
    
    // Check if at least ESPN or D11 is selected (as required by backend)
    const hasEspnOrD11 = selected['espn'] || selected['d11'];
    if (!hasEspnOrD11) {
      setError('At least one ESPN or Dream11 tournament must be selected');
      return;
    }
    
    setCreating(true);
    setError(null);
    setSuccess(null);
    
    try {
      // Convert to new format like {d11_tournament_id: id, espn_tournament_id: id, etc}
      const payload: Record<string, string> = {};
      
      Object.entries(selected).forEach(([platform, selection]) => {
          payload[platform] = selection.id;
      });
      
      const res = await seriesApi.createFromPlatform(payload);
      
      if (res.data && res.data.series_id) {
        setSuccess({ id: res.data.series_id, name: res.data.series_name });
        enqueueSnackbar(res.data.message, { variant: 'success' });
        
        // Show which platforms were attached
        const attachedPlatforms = Object.entries(res.data.attached_platforms || {})
          .filter(([_, attached]) => attached)
          .map(([platform, _]) => platform)
          .join(', ');
          
        if (attachedPlatforms) {
          enqueueSnackbar(`Platforms attached: ${attachedPlatforms}`, { variant: 'info' });
        }
      } else {
        setError(res.data?.error || 'Failed to create series');
      }
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Failed to create series');
    } finally {
      setCreating(false);
    }
  };

  const renderColumn = (platform: string, list: any[]) => {
    const sel = selected[platform];
    // Find the selected item in the list
    const selectedItem = sel ? list.find((item: any) => String(item.platform_id) === sel.id) : null;
    // Remove the selected item from the list for rendering below
    const rest = selectedItem ? list.filter((item: any) => String(item.platform_id) !== sel.id) : list;
    return (
      <Box key={platform} sx={{ minWidth: 200, maxWidth: 260, flex: 1, px: 0.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, textAlign: 'center', fontSize: 15 }}>
          {PLATFORMS.find(p => p.id === platform)?.label}
        </Typography>
        {/* Selected summary or 'No series selected' */}
        {selectedItem ? (
          <Paper
            variant='outlined'
            sx={{
              p: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: theme.palette.action.selected,
              border: `2px solid ${theme.palette.primary.main}`,
              boxShadow: 4,
              minHeight: 36,
              mb: 0.5,
            }}
          >
            <Box>
              <Typography fontWeight={500} sx={{ fontSize: 14, lineHeight: 1.2 }}>{selectedItem.name}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                {platform === 'mt11' ? null : <>ID: {selectedItem.platform_id}</>}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11, display: 'block' }}>
                {format(new Date(selectedItem.start_date), 'MMM dd, yyyy')} - {format(new Date(selectedItem.end_date), 'MMM dd, yyyy')}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {selectedItem.series_id ? (
                <Chip label={`${selectedItem.series_id}`} color="success" size="small" sx={{ fontSize: 11, height: 20 }} />
              ) : (
                <Chip label="N/A" color="warning" size="small" sx={{ fontSize: 11, height: 20 }} />
              )}
              <Tooltip title="Deselect">
                <IconButton
                  onClick={() => handleDeselect(platform)}
                  size="small"
                  sx={{ minWidth: 0, p: 0.2 }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Paper>
        ) : (
          <Box sx={{ mb: 0.5, textAlign: 'center', color: theme.palette.text.disabled, fontSize: 13 }}>
            No series selected
          </Box>
        )}
        <Stack spacing={0.5}>
          {rest.length === 0 && <Typography color="text.secondary" sx={{ textAlign: 'center', fontSize: 13 }}>No tournaments found.</Typography>}
          {rest.map((item) => {
            const isSelected = sel && sel.id === String(item.platform_id);
            const notAttached = !item.series_id;
            return (
              <React.Fragment key={item.platform_id}>
                <Tooltip title={notAttached ? 'Not attached' : `Attached (Series: ${item.series_id})`} arrow>
                  <Paper
                    variant={'elevation'}
                    sx={{
                      p: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      background: isSelected
                        ? theme.palette.action.selected: undefined,
                      boxShadow: isSelected ? 4 : 1,
                      border: isSelected ? `2px solid ${theme.palette.primary.main}` : undefined,
                      cursor: 'pointer',
                      opacity: 1,
                      minHeight: 36,
                      transition: 'background 0.2s, box-shadow 0.2s',
                    }}
                    onClick={() => handleSelect(platform, String(item.platform_id), item)}
                  >
                    <Box>
                      <Typography fontWeight={500} sx={{ fontSize: 14, lineHeight: 1.2 }}>{item.name}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                        {platform === 'mt11' ? null :  <>ID: {item.platform_id}</>}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11, display: 'block' }}>
                        {format(new Date(item.start_date), 'MMM dd, yyyy')} - {format(new Date(item.end_date), 'MMM dd, yyyy')}
                      </Typography>
                    </Box>
                    <Box>
                      {notAttached ? (
                        <Chip label="N/A" color="warning" size="small" sx={{ fontSize: 11, height: 20 }} />
                      ) : (
                        <Chip label={`${item.series_id}`} color="success" size="small" sx={{ fontSize: 11, height: 20 }} />
                      )}
                    </Box>
                  </Paper>
                </Tooltip>
              </React.Fragment>
            );
          })}
        </Stack>
      </Box>
    );
  };

  return (
    <Box sx={{ mx: '5px', p: { xs: 0.5, sm: 1, md: 1 } }}>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
        <Button
          variant="contained"
          color="primary"
          disabled={Object.keys(selected).length === 0 || creating || (!selected['espn'] && !selected['d11'])}
          onClick={handleCreate}
          sx={{ fontSize: 13, minHeight: 32, px: 2 }}
        >
          {creating ? <CircularProgress size={18} /> : `Create Series (${Object.keys(selected).length} selected${!selected['espn'] && !selected['d11'] ? ' - Need ESPN or D11' : ''})`}
        </Button>
        {success && (
          <Alert severity="success" sx={{ flex: 1, fontSize: 13 }}>
            Series "{success.name}" created successfully! (ID: {success.id})
          </Alert>
        )}
        {error && !loading && (
          <Alert severity="error" sx={{ flex: 1, fontSize: 13 }}>{error}</Alert>
        )}
      </Box>
      <Typography variant="h6" fontWeight={700} mb={1} sx={{ fontSize: 18 }}>Create New Series (Cricket)</Typography>
      <Typography variant="body2" color="text.secondary" mb={2} sx={{ fontSize: 13 }}>
        Select tournaments from different platforms. <strong>At least one ESPN or Dream11 tournament is required.</strong> Other platforms will be attached to the created series.
      </Typography>
      <Paper sx={{ p: 1, mb: 2 }}>
        <TextField
          label="Search by Series Name"
          variant="outlined"
          size="small"
          value={search}
          onChange={e => setSearch(e.target.value)}
          sx={{ mb: 1, width: 220, fontSize: 13 }}
          InputProps={{ style: { fontSize: 13 } }}
          InputLabelProps={{ style: { fontSize: 13 } }}
        />
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={22} />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ fontSize: 13 }}>{error}</Alert>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: 1,
              alignItems: 'flex-start',
              justifyContent: 'stretch',
              width: '100%',
              overflowX: 'auto',
            }}
          >
            {PLATFORMS.map((p) => renderColumn(p.id, filteredOptions[p.id] || []))}
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default NewSeries; 